"use client";

import { c as _c } from "react/compiler-runtime";
import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo } from "react";
import { callstackSlice } from "#/features/callstack/model/callstackSlice";
import { projectSlice } from "#/features/project/model/projectSlice";
import { useHasMounted, usePrevious } from "#/shared/hooks";
import { getLastPlaygroundPath, isValidLastPlaygroundPath } from "#/shared/local-storage/playgroundPath";
import { useAppDispatch } from "#/store/hooks";
import { isPlaygroundView, type PlaygroundView, usePlaygroundViewParam } from "./usePlaygroundViewParam";
export type { PlaygroundView } from "./usePlaygroundViewParam";

/**
 * Manages the mobile playground phase via the `?view=` query parameter.
 *
 * - **Implicit code with a slug**: if `view` is absent, shallow `replace` adds `?view=code`.
 * - **Tab changes**: shallow `router.replace` (no extra history entries per tab tap).
 */
export const useMobilePlaygroundView = () => {
  const $ = _c(30);
  const router = useRouter();
  const dispatch = useAppDispatch();
  const hasMounted = useHasMounted();
  const {
    view,
    setView
  } = usePlaygroundViewParam();
  const slugs = router.query.slug;
  const hasProjectSlug = Array.isArray(slugs) && slugs.length > 0;
  let t0;
  bb0: {
    if (view && isPlaygroundView(view)) {
      t0 = view;
      break bb0;
    }
    if (hasProjectSlug) {
      t0 = "code";
      break bb0;
    }
    if (!hasMounted) {
      t0 = "browse";
      break bb0;
    }
    t0 = isValidLastPlaygroundPath(getLastPlaygroundPath()) ? "code" : "browse";
  }
  const currentView = t0;
  const previousView = usePrevious(currentView);
  let t1;
  if ($[0] !== hasProjectSlug || $[1] !== router.isReady || $[2] !== setView || $[3] !== view) {
    t1 = () => {
      if (!router.isReady) {
        return;
      }
      if (!hasProjectSlug) {
        return;
      }
      if (view !== "") {
        return;
      }
      setView("code", {
        replace: true
      });
    };
    $[0] = hasProjectSlug;
    $[1] = router.isReady;
    $[2] = setView;
    $[3] = view;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== hasProjectSlug || $[6] !== router || $[7] !== setView || $[8] !== view) {
    t2 = [router.isReady, hasProjectSlug, view, setView, router];
    $[5] = hasProjectSlug;
    $[6] = router;
    $[7] = setView;
    $[8] = view;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  useEffect(t1, t2);
  let t3;
  if ($[10] !== setView) {
    t3 = (targetView, pathName) => {
      setView(targetView, {
        replace: true,
        pathName
      });
    };
    $[10] = setView;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const navigateTo = t3;
  let t4;
  if ($[12] !== navigateTo) {
    t4 = () => navigateTo("browse");
    $[12] = navigateTo;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const goToBrowse = t4;
  let t5;
  if ($[14] !== dispatch || $[15] !== navigateTo) {
    t5 = projectSlug => {
      const pathName_0 = projectSlug ? `/playground/${projectSlug}` : undefined;
      if (projectSlug) {
        dispatch(projectSlice.actions.loadStart());
      }
      navigateTo("code", pathName_0);
    };
    $[14] = dispatch;
    $[15] = navigateTo;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const goToCode = t5;
  let t6;
  if ($[17] !== navigateTo) {
    t6 = () => navigateTo("results");
    $[17] = navigateTo;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const goToResults = t6;
  let t7;
  let t8;
  if ($[19] !== currentView || $[20] !== dispatch || $[21] !== previousView) {
    t7 = () => {
      if (previousView === "results" && currentView !== "results") {
        dispatch(callstackSlice.actions.setIsPlaying(false));
      }
    };
    t8 = [currentView, previousView, dispatch];
    $[19] = currentView;
    $[20] = dispatch;
    $[21] = previousView;
    $[22] = t7;
    $[23] = t8;
  } else {
    t7 = $[22];
    t8 = $[23];
  }
  useEffect(t7, t8);
  let t9;
  if ($[24] !== currentView || $[25] !== goToBrowse || $[26] !== goToCode || $[27] !== goToResults || $[28] !== hasProjectSlug) {
    t9 = {
      currentView,
      hasProjectSlug,
      goToBrowse,
      goToCode,
      goToResults
    };
    $[24] = currentView;
    $[25] = goToBrowse;
    $[26] = goToCode;
    $[27] = goToResults;
    $[28] = hasProjectSlug;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  return t9 as const;
};